import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Space } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { StyledModal, StyledInput, FormSection, PrimaryButton, CancelButton, ErrorText, FieldLabel } from './styles';
import CategorySelector from './CategorySelector';
import SchedulesSelector from './SchedulesSelector';
import GoogleMapsAddressInput from './GoogleMapsAddressInput';
import type { StoreFormProps } from './types';
import type { Category, Schedule, CreateStorePayload, UpdateStorePayload } from './store/userStoresSlice';
import type { AddressErrors } from './GoogleMapsAddressInput/types';
import { LABELS, VALIDATION_MESSAGES, PLACEHOLDERS } from './constants';

type FormValues = {
  name: string;
  categories: Category[];
  schedules: Schedule[];
};

const StoreForm: React.FC<StoreFormProps> = ({ store, categories, schedules, loading, onSubmit, onCancel }) => {
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});

  const [currentStoreData, setCurrentStoreData] = useState(() => ({
    address: store?.address || '',
    streetName: store?.streetName || '',
    streetNumber: store?.streetNumber || '',
    apartment: store?.apartment || '',
    city: store?.city || '',
    state: store?.state || '',
    zipCode: store?.zipCode || '',
    phoneNumber: store?.phoneNumber || '',
    latitude: Number(store?.latitude) || 0,
    longitude: Number(store?.longitude) || 0,
  }));

  const { control, handleSubmit, formState: { errors }, setValue, watch, reset, register } = useForm<FormValues>({
    defaultValues: { name: '', categories: [], schedules: [] },
  });

  register('categories', { validate: (v) => v.length > 0 || VALIDATION_MESSAGES.CATEGORY_REQUIRED });
  register('schedules', { validate: (v) => v.length > 0 || VALIDATION_MESSAGES.SCHEDULE_REQUIRED });

  const selectedCategories = watch('categories');
  const selectedSchedules = watch('schedules');

  useEffect(() => {
    if (store) {
      reset({
        name: store.name || '',
        categories: store.categories || [],
        schedules: store.schedules || [],
      });
      setCurrentStoreData({
        address: store.address || '',
        streetName: store.streetName || '',
        streetNumber: store.streetNumber || '',
        apartment: store.apartment || '',
        city: store.city || '',
        state: store.state || '',
        zipCode: store.zipCode || '',
        phoneNumber: store.phoneNumber || '',
        latitude: store.latitude || 0,
        longitude: store.longitude || 0,
      });
    } else {
      reset({ name: '', categories: [], schedules: [] });
      setCurrentStoreData({
        address: '',
        streetName: '',
        streetNumber: '',
        apartment: '',
        city: '',
        state: '',
        zipCode: '',
        phoneNumber: '',
        latitude: 0,
        longitude: 0,
      });
    }
  }, [store, reset]);

  useEffect(() => {
    setAddressErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      if (currentStoreData.streetNumber?.trim()) delete next.streetNumber;
      if (currentStoreData.apartment?.trim()) delete next.apartment;
      if (currentStoreData.city?.trim()) delete next.city;
      if (currentStoreData.zipCode?.trim()) delete next.zipCode;
      if (currentStoreData.phoneNumber?.trim()) delete next.phoneNumber;
      return next;
    });
  }, [currentStoreData]);

  const onFormSubmit = (values: FormValues) => {
    const addrErrors: AddressErrors = {};
    if (!currentStoreData.streetNumber?.trim()) addrErrors.streetNumber = 'Street Number is required';
    if (!currentStoreData.apartment?.trim()) addrErrors.apartment = 'Apt/Unit is required';
    if (!currentStoreData.city?.trim()) addrErrors.city = 'City is required';
    if (!currentStoreData.zipCode?.trim()) addrErrors.zipCode = 'ZIP Code is required';
    if (!currentStoreData.phoneNumber?.trim()) addrErrors.phoneNumber = 'Phone Number is required';

    if (Object.keys(addrErrors).length > 0) {
      setAddressErrors(addrErrors);
      return;
    }

    setAddressErrors({});
    const payload: CreateStorePayload | UpdateStorePayload = {
      ...(store ? { id: store.id } : {}),
      name: values.name.trim(),
      address: currentStoreData.address?.trim() || '',
      city: currentStoreData.city?.trim() || '',
      phoneNumber: currentStoreData.phoneNumber?.trim() || '',
      streetName: currentStoreData.streetName?.trim() || '',
      streetNumber: currentStoreData.streetNumber?.trim() || '',
      apartment: currentStoreData.apartment?.trim() || '',
      state: currentStoreData.state?.trim() || '',
      zipCode: currentStoreData.zipCode?.trim() || '',
      categories: values.categories.map((cat) => cat.id),
      schedules: values.schedules.map((sch) => sch.id),
      latitude: currentStoreData.latitude || 0,
      longitude: currentStoreData.longitude || 0,
    };
    onSubmit(payload);
  };

  return (
    <StyledModal
      title={
        <Space>
          <span>🏪</span>
          <span>{store ? LABELS.UPDATE_STORE : LABELS.CREATE_STORE}</span>
        </Space>
      }
      open={true}
      onCancel={onCancel}
      maskClosable={false}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CancelButton key="cancel" onClick={onCancel} disabled={loading} style={{ height: 40, paddingInline: 24, marginLeft: 0 }}>
            {LABELS.CANCEL}
          </CancelButton>
          <PrimaryButton
            key="submit"
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleSubmit(onFormSubmit)}
            loading={loading}
            style={{ height: 40, paddingInline: 24 }}
          >
            {store ? LABELS.UPDATE : LABELS.CREATE}
          </PrimaryButton>
        </div>
      }
      width={800}
      destroyOnClose
    >
      <FormSection>
        <div>
          <FieldLabel>{LABELS.STORE_NAME} <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
          <Controller
            name="name"
            control={control}
            rules={{ required: VALIDATION_MESSAGES.STORE_NAME_REQUIRED }}
            render={({ field }) => (
              <StyledInput
                {...field}
                placeholder={PLACEHOLDERS.STORE_NAME}
                status={errors.name ? 'error' : ''}
              />
            )}
          />
          {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
        </div>

        <div>
          <FieldLabel>{LABELS.STORE_ADDRESS} <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
          <GoogleMapsAddressInput
            currentStore={currentStoreData}
            setCurrentStore={setCurrentStoreData}
            addressErrors={addressErrors}
          />
        </div>

        <CategorySelector
          categories={categories}
          selectedCategories={selectedCategories}
          onCategoryChange={(cats) => setValue('categories', cats, { shouldValidate: true })}
          error={errors.categories?.message as string | undefined}
        />

        <SchedulesSelector
          schedules={schedules}
          selectedSchedules={selectedSchedules}
          onScheduleChange={(schs) => setValue('schedules', schs, { shouldValidate: true })}
          error={errors.schedules?.message as string | undefined}
        />


      </FormSection>
    </StyledModal>
  );
};

export default React.memo(StoreForm);
